<script>
import BookingCreateHeaderV2 from '@/components/booking/booking-create-header-v2.vue'
import BookingCreateMeta from '@/components/booking/booking-create-meta.vue'
import BookingCreateMetaV2 from '@/components/booking/booking-create-meta-v2.vue'
import BookingCreateSummaryCard from '@/components/booking/booking-create-summary-card.vue'
import BookingFetchError from '@/components/booking/booking-fetch-error.vue'
import BookingLocationList from '@/components/booking/booking-location-list.vue'
import PromiseConfirmDialog from '@/components/general/promise-confirm-dialog.vue'
import PromiseDialogOld from '@/components/general/promise-dialog-old.vue'
import BookingLoader from '@/components/skeleton/booking-loader.vue'
import baseMixin from '@/mixins/baseMixin'
import idleMixin from '@/mixins/idleMixin'
import dayjs from '@/services/date'
import { getBookingErrorType } from '@/shared/constants/booking/BookingErrorType'
import { UNSAVED_DIALOG_ACTIONS } from '@/shared/constants/dialog/UnsavedDialogActions'
import routeDefinitions from '@/shared/constants/routes/routeDefinitions'
import Layout from '@layouts/main.vue'
import { isEmpty } from 'lodash'
import { mapActions, mapGetters } from 'vuex'
import FeatureFlags from '@/shared/constants/features/AzureFeatureFlags'
const { RevampedCreateBooking } = FeatureFlags

export default {
  name: 'BookingCreateView',
  metaInfo() {
    return {
      title: this.$route?.meta?.title,
      meta: [{ name: 'description', content: 'The Booking Create page.' }],
    }
  },
  components: {
    Layout,
    BookingCreateHeaderV2,
    BookingCreateMeta,
    BookingCreateMetaV2,
    BookingLocationList,
    BookingCreateSummaryCard,
    PromiseDialogOld,
    BookingLoader,
    PromiseConfirmDialog,
    BookingFetchError,
  },
  mixins: [baseMixin, idleMixin],
  data: () => {
    return {
      selectedLocation: null,
      formChanged: false,
      editFormChanged: false,
      showSummaryPage: false,
      summaryData: null,
      preselectDate: null,
      bookingId: null,
      booking: null,
      loadingBooking: false,
      bookingFetchError: null,
    }
  },
  computed: {
    ...mapGetters({
      locations: 'locations/bookingLocations',
      isLoadingBookingLocations: 'locations/isLoadingBookingLocations',
      isFeatureEnabled: 'features/isFeatureEnabled',
    }),
    bookingMode() {
      return this.$route.query?.mode
    },
    isEditMode() {
      return this.$route.name === routeDefinitions.bookingsEdit.name
    },
    isDuplicateMode() {
      return this.$route.name === routeDefinitions.bookingsDuplicate.name
    },
    isBookingDetailsLoading() {
      if (this.isEditMode || this.isDuplicateMode) {
        return this.isLoadingBookingLocations || this.loadingBooking
      }
      return false
    },
    isCreateBookingV2FeatureFlagEnabled() {
      return this.isFeatureEnabled(RevampedCreateBooking)
    },
    showCreateBookingV2Header() {
      return (
        this.isCreateBookingV2FeatureFlagEnabled &&
        !this.isBookingDetailsLoading &&
        !this.bookingFetchError &&
        this.selectedLocation &&
        !this.showSummaryPage
      )
    },
  },
  watch: {
    idleMixin_isIdle(v, o) {
      this.loadLocationBookingBlocks()
    },
  },
  async created() {
    await this.getFeatureFlag(RevampedCreateBooking)

    this.validatePreselectDate()
    const { query, params } = this.$route

    this.loadLocationBookingBlocks()
    const result = await this.loadBookingLocations()

    // Auto select location if only one was returned
    if (this.hasSingleLocation(result)) this.selectLocation(result.data[0])

    if (this.isEditMode || this.isDuplicateMode) {
      this.loadingBooking = true
      this.bookingId = params.bookingId

      const dateQuery = query.date ? dayjs(query.date) : null
      if (this.isEditMode || (dateQuery && dateQuery.isAfter(dayjs()))) {
        this.preselectDate = query.date
      }
      await this.loadBookingDetails(this.bookingId)
    }
  },
  async beforeRouteLeave(to, from, next) {
    const isChanged = this.isEditMode ? this.editFormChanged : this.formChanged
    if (!isChanged) return next()

    const result = await this.showUnsavedDialog()

    if (result === 'save') {
      next(false)

      this.$nextTick(() => {
        window.scrollTo({
          top: document.body.scrollHeight,
          behavior: 'smooth',
        })
      })
      if (this.isCreateBookingV2FeatureFlagEnabled) {
        await this.requestCreateBookingV2Submit()
      } else {
        await this.$refs.bookingCreateMeta.submit()
      }
      this.updateDirtyState(false)
      return
    }

    if (result === 'discard') {
      this.resetCreateBookingV2SendToANZUK()

      this.updateDirtyState(false)
      return next()
    }

    next(false)
  },
  methods: {
    ...mapActions({
      loadBookingLocations: 'locations/loadBookingLocations',
      loadLocationBookingBlocks: 'locations/loadLocationBookingBlocks',
      setRecentLocation: 'bookings/setRecentLocation',
      getBookingDetailById: 'bookings/getBookingDetailById',
      getFeatureFlag: 'features/getFeatureFlag',
      requestCreateBookingV2Submit: 'bookings/requestCreateBookingV2Submit',
      resetCreateBookingV2SendToANZUK:
        'bookings/resetCreateBookingV2SendToANZUK',
    }),
    async showUnsavedDialog() {
      return await this.$refs.editFormUnsavedChangesDialog.show({
        title: this.$t('general.unsavedChangesDialogConfirmation.title'),
        text: this.$t('general.unsavedChangesDialogConfirmation.desc'),
        icon: 'mdi-alert-outline',
        iconClass: 'icon-warning',
        actions: UNSAVED_DIALOG_ACTIONS(this.$t.bind(this)),
      })
    },
    async loadBookingDetails(bookingId) {
      try {
        const response = await this.getBookingDetailById(bookingId)
        if (response && response.isSuccess && response.data) {
          this.booking = response.data
          const bookingLocation = this.locations.find(
            (x) => x.locationId === this.booking.clientLocation.locationId
          )
          if (bookingLocation) {
            this.selectLocation(bookingLocation)
          }
        } else {
          const status = response.error?.status
          const err = getBookingErrorType(status, this.$t.bind(this), bookingId)

          this.bookingFetchError = {
            status: status,
            code: response.error?.data?.code,
            title: err.title,
            subTitle: err.message,
          }
        }
      } finally {
        this.loadingBooking = false
      }
    },
    validatePreselectDate() {
      if (
        !this.$route.query ||
        !Object.prototype.hasOwnProperty.call(this.$route.query, 'date')
      )
        return

      const dateQuery = dayjs(this.$route.query.date)
      const isPast = dateQuery.isBefore(dayjs())

      if (isPast) return
      this.preselectDate = this.$route.query.date
    },
    selectLocation(location) {
      this.selectedLocation = location
      this.setRecentLocation(location.locationId)
    },
    changeLocation() {
      this.resetCreateBookingV2SendToANZUK()

      this.selectedLocation = null
    },
    hasSingleLocation(resultObj) {
      return (
        resultObj.isSuccess &&
        !isEmpty(resultObj.data) &&
        Array.isArray(resultObj.data) &&
        resultObj.data.length === 1
      )
    },
    handleDisplaySummaryPage(summaryObj) {
      this.resetCreateBookingV2SendToANZUK()

      this.formChanged = false
      this.summaryData = summaryObj
      this.showSummaryPage = true
    },
    createNewBooking() {
      this.showSummaryPage = false
      if (this.hasSingleLocation({ isSuccess: true, data: this.locations }))
        this.selectLocation(this.locations[0])
      else this.changeLocation()
      this.summaryData = null
    },
    async handleBackClick() {
      const isDirty = this.isEditMode ? this.editFormChanged : this.formChanged

      if (!isDirty) {
        this.handlePrevious()
        return
      }

      const result = await this.showUnsavedDialog()
      if (result === 'save') {
        this.$nextTick(() => {
          window.scrollTo({
            top: document.body.scrollHeight,
            behavior: 'smooth',
          })
        })

        if (this.isCreateBookingV2FeatureFlagEnabled) {
          await this.requestCreateBookingV2Submit()
        } else {
          await this.$refs.bookingCreateMeta.submit()
        }
        this.updateDirtyState(false)
        return
      }

      if (result === 'discard') {
        this.resetCreateBookingV2SendToANZUK()

        this.updateDirtyState(false)
        this.handlePrevious()
      }
    },
    updateDirtyState(value) {
      if (this.isEditMode) this.editFormChanged = value
      else this.formChanged = value
    },
    handlePrevious() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.push({ name: routeDefinitions.home.name })
      }
    },
  },
}
</script>

<template>
  <Layout>
    <!-- Skeleton loader while fetching booking -->
    <template v-if="showCreateBookingV2Header" v-slot:header>
      <BookingCreateHeaderV2
        :edit-mode="isEditMode"
        :booking-id="booking ? booking.id : null"
        @back-to-details="changeLocation"
      />
    </template>
    <template v-if="isBookingDetailsLoading">
      <BookingLoader />
    </template>
    <template v-else-if="bookingFetchError">
      <BookingFetchError
        :error="bookingFetchError"
        @backClicked="handleBackClick"
      />
    </template>
    <template v-else>
      <BookingCreateSummaryCard
        v-if="showSummaryPage"
        :edit-mode="isEditMode"
        :booking-mode="bookingMode"
        :summary-data="summaryData"
        :is-classification-hidden="
          summaryData && summaryData.isClassificationHidden
        "
        @createNewBooking="createNewBooking"
      />
      <BookingLocationList
        v-else-if="!selectedLocation"
        @selectLocation="selectLocation"
      />
      <BookingCreateMetaV2
        v-else-if="isCreateBookingV2FeatureFlagEnabled"
        ref="bookingCreateMeta"
        :selected-location="selectedLocation"
        :preselect-date="preselectDate"
        :edit-mode="isEditMode"
        :duplicate-mode="isDuplicateMode"
        :booking-mode="bookingMode"
        :booking="booking"
        @showSummaryPage="handleDisplaySummaryPage"
        @changeLocation="changeLocation"
        @updated="formChanged = true"
        @backClicked="handleBackClick"
        @editChanged="editFormChanged = $event"
      />
      <BookingCreateMeta
        v-else
        ref="bookingCreateMeta"
        :selected-location="selectedLocation"
        :preselect-date="preselectDate"
        :edit-mode="isEditMode"
        :booking-mode="bookingMode"
        :booking="booking"
        @showSummaryPage="handleDisplaySummaryPage"
        @changeLocation="changeLocation"
        @updated="formChanged = true"
        @backClicked="handleBackClick"
        @editChanged="editFormChanged = $event"
      />
      <PromiseDialogOld ref="unsavedChangesDialog" />
      <PromiseConfirmDialog ref="editFormUnsavedChangesDialog" :width="'544'" />
    </template>
  </Layout>
</template>
